import {
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
} from '@material-ui/core'
import { ChevronRight } from '@material-ui/icons'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { PROJECT_CURRENT_STEP } from '../../../constants/projectConstants'
import { AddressType } from '../../../enum'

interface AddressProps {
  address: string
  type: AddressType
}

interface DeliveryDetailsProps {
  isRequestSummary?: AddOnServices['isRequestSummary']
}

const DeliveryDetails = ({ isRequestSummary }: DeliveryDetailsProps) => {
  const dispatch = useDispatch()

  const [expanded, setExpanded] = useState(isRequestSummary)
  const [shippingAddress, setShippingAddress] = useState<Address | undefined>(
    undefined
  )
  const [returnAddress, setReturnAddress] = useState<Address | undefined>(
    undefined
  )

  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  useEffect(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const getAddress = async ({ address, type }: AddressProps) => {
      try {
        const { data } = await axios.get(`/api/address/edit/${address}`, config)

        if (type === 'Shipping') {
          setShippingAddress(data)
        }

        if (type === 'Return') {
          setReturnAddress(data)
        }
      } catch (error) {
        console.error(error)
      }
    }

    if (project) {
      getAddress({
        address: project.deliveryDetails.shippingAddress,
        type: AddressType.SHIPPING,
      })
      getAddress({
        address: project.deliveryDetails.returnAddress,
        type: AddressType.RETURN,
      })
    }
  }, [project, userInfo])

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const formatDate = (date: Project['deliveryDate']) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
    return new Date(date).toLocaleDateString([], options)
  }

  return (
    <div className="request">
      <div className="flex space-between">
        <div className="flex">
          <div>
            <IconButton
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              className={`m-r-10 ${expanded ? 'rotate' : ''}`}
            >
              <ChevronRight />
            </IconButton>
          </div>
          <div>
            <h4 className="m-b-0">Delivery details</h4>
            <p className="m-b-0 gray-text fs14">
              Timeframe and delivery details
            </p>
          </div>
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Card className="summary-card">
          <div className="flex space-between align-center header">
            <strong>Timeframe</strong>

            <Button
              color="primary"
              onClick={() =>
                dispatch({
                  type: PROJECT_CURRENT_STEP,
                  payload: 6,
                })
              }
            >
              <span className="underline"> Edit</span>
            </Button>
          </div>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                Desired due date
              </Grid>

              <Grid item xs={6}>
                {formatDate(project.deliveryDate)}
              </Grid>

              <Grid item xs={6}>
                Additional notes
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDateComments}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className="summary-card">
          <div className="flex space-between header align-center">
            <strong>Delivery details</strong>

            <Button
              color="primary"
              onClick={() =>
                dispatch({
                  type: PROJECT_CURRENT_STEP,
                  payload: 7,
                })
              }
            >
              <span className="underline"> Edit</span>
            </Button>
          </div>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                Shipping address
              </Grid>

              <Grid item xs={6}>
                {shippingAddress && (
                  <>
                    {shippingAddress.companyName && (
                      <div>{shippingAddress.companyName}</div>
                    )}
                    <div>
                      {shippingAddress.streetAddress &&
                        shippingAddress.streetAddress}{' '}
                      {shippingAddress.aptSuiteOther !== '' &&
                        `, ${shippingAddress.aptSuiteOther}`}
                    </div>
                    <div>
                      {shippingAddress.city && shippingAddress.city},{' '}
                      {shippingAddress.state && shippingAddress.state}{' '}
                      {shippingAddress.zipCode && shippingAddress.zipCode}
                    </div>
                  </>
                )}
              </Grid>

              <Grid item xs={6}>
                Return address
              </Grid>

              <Grid item xs={6}>
                {returnAddress && (
                  <>
                    {returnAddress.companyName && (
                      <div>{returnAddress.companyName}</div>
                    )}
                    <div>
                      {returnAddress.streetAddress &&
                        returnAddress.streetAddress}{' '}
                      {returnAddress.aptSuiteOther !== '' &&
                        `, ${returnAddress.aptSuiteOther}`}
                    </div>
                    <div>
                      {returnAddress.city && returnAddress.city},{' '}
                      {returnAddress.state && returnAddress.state}{' '}
                      {returnAddress.zipCode && returnAddress.zipCode}
                    </div>
                  </>
                )}
              </Grid>

              <Grid item xs={6}>
                Carrier
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDetails.carrier}
              </Grid>

              <Grid item xs={6}>
                Carrier account number
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDetails.accountNumber}
              </Grid>

              <Grid item xs={6}>
                Shipping method
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDetails.shippingMethod}
              </Grid>

              <Grid item xs={6}>
                Expedited shipping
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDetails.expedited ? 'Yes' : 'No'}
              </Grid>

              <Grid item xs={6}>
                Additional notes
              </Grid>

              <Grid item xs={6}>
                {project.deliveryDetails.additionalComments}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>
    </div>
  )
}

export default DeliveryDetails
